import { unitById, type Role } from "@zend/content";
import { battleMorale, evaluateChemistry, type SquadMember } from "@zend/rules";
import { SQUAD_SLOT_KEYS, type SquadSlotKey, type SquadView, type UnitView } from "./types.js";

export const SLOT_ROLES: Record<SquadSlotKey, readonly Role[]> = {
  commander: ["commander"],
  f1: ["defender", "attacker", "saboteur"], f2: ["defender", "attacker", "saboteur"],
  f3: ["defender", "attacker", "saboteur"], f4: ["defender", "attacker", "saboteur"],
  s1: ["support"], s2: ["support"],
};

/** Assignment is legal across home squads, including another squad's identically named slot. */
export function slotCandidates(units: UnitView[], slot: SquadSlotKey): UnitView[] {
  return units.filter(unit => SLOT_ROLES[slot].includes(unitById(unit.unitId).role)).sort((a, b) => b.power - a.power);
}

export function assignSlot(squad: SquadView | null, slot: SquadSlotKey, rowId: string | null): Record<SquadSlotKey, string | null> {
  const members = Object.fromEntries(SQUAD_SLOT_KEYS.map(key => [key, squad?.members[key] ?? null])) as Record<SquadSlotKey, string | null>;
  if (rowId !== null) for (const key of SQUAD_SLOT_KEYS) if (members[key] === rowId) members[key] = null;
  members[slot] = rowId;
  return members;
}

/** Read-only draft: identical chemistry inputs to the server; no estimated power or mutation. */
export function formationEffects(members: Record<SquadSlotKey, string | null>, units: ReadonlyMap<string, UnitView>) {
  const rows = SQUAD_SLOT_KEYS.flatMap(slot => {
    const row = members[slot] ? units.get(members[slot]!) : undefined;
    return row ? [{ slot, row, def: unitById(row.unitId) }] : [];
  });
  const chemistry = evaluateChemistry(rows.map(({ slot, row, def }): SquadMember => ({
    unitId: row.id, mfr: def.mfr, role: def.role, chassis: def.chassis,
    slot: slot === "commander" ? "commander" : slot.startsWith("s") ? "support" : "fighter",
  })));
  const morale = battleMorale(rows.filter(({ row }) => !row.wrecked && !row.repair)
    .map(({ slot, def }) => ({ kind: "unit", slot, role: def.role, alive: true })));
  return { chemistry, morale };
}

/** Display only; the server still checks reservations and combat readiness on every mutation. */
export function squadReadiness(squad: SquadView | null, units: UnitView[], deployed: ReadonlySet<number> | null): "checking" | "deployed" | "reserved" | "repairing" | "ready" | "incomplete" {
  if (squad?.siegeRunId) return "reserved";
  if (!deployed) return "checking";
  if (squad && deployed.has(squad.slot)) return "deployed";
  if (squad && units.some(unit => Object.values(squad.members).includes(unit.id) && unit.repair)) return "repairing";
  return squad?.ready ? "ready" : "incomplete";
}
